import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AGORA_APP_ID } from '../../config';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';

import AgoraRTC, {
  LocalUser,
  useJoin,
  useLocalMicrophoneTrack,
  useLocalCameraTrack,
  usePublish,
  useRTCClient
} from 'agora-rtc-react';

import AgoraProvider, { client } from '../../context/AgoraProvider';
import streamChatService from '../../services/stream-chat.service';
import { useSelector } from 'react-redux';
import { formatViewers } from './utils/format-number';
import { useMemo } from 'react';
import DotSpinner from '../../components/shared/DotSpinner';

const InteractiveLiveStreaming = () => {
  useEffect(() => {
    client.setClientRole('host');
  }, []);
  return (
    <AgoraProvider>
      <StreamBroadcaster />
    </AgoraProvider>
  );
};

const configStreamState = {
  created: 'created',
  edit: 'edit',
  active: 'active',
  disabled: 'disabled'
};

const StreamBroadcaster = () => {
  // Support hook
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.auth_token);

  // Stream state
  const [streamId, setStreamId] = useState('');
  const [streamState, setStreamState] = useState(configStreamState.created);
  const [isLoading, setIsLoading] = useState(false);

  const [viewerCount, setViewerCount] = useState(0);
  const [giftsCount, setGiftsCount] = useState(0);

  const [secondsLeft, setSecondsLeft] = useState(null);
  const [startedAt, setStartedAt] = useState(null);

  // Stream message
  const [chatMessages, setChatMessages] = useState([]);
  const bottomRef = useRef();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const [streamTitle, setStreamTitle] = useState('');

  // Agora state
  const [agoraToken, setAgoraToken] = useState('');
  const [agoraChannel, setAgoraChannel] = useState('');
  const [micOn, setMic] = useState(true);
  const [cameraOn, setCamera] = useState(true);
  const [cameraIndex, setCameraIndex] = useState(0);

  const rtcClient = useRTCClient();
  const { localMicrophoneTrack } = useLocalMicrophoneTrack(micOn);
  const { localCameraTrack } = useLocalCameraTrack(cameraOn);

  const calling = Boolean(agoraToken && agoraChannel);

  usePublish([localMicrophoneTrack, localCameraTrack]);

  const {
    data,
    error,
    isConnected,
    isLoading: isLoadingAgoraJoin
  } = useJoin(
    {
      appid: AGORA_APP_ID,
      channel: agoraChannel,
      token: agoraToken
    },
    calling
  );

  const isLoadStream = isLoadingAgoraJoin || isLoading;

  useEffect(() => {
    if (!error) return;
    console.error('[StreamBroadcaster] Agora error:', error);
    toast.error(error.message);
  }, [error]);

  useEffect(() => {
    console.log('[StreamBroadcaster] Agora connected:', isConnected);

    if (!isConnected) {
      initConnectToStream();
      return;
    }
    setStreamState(configStreamState.active);
    initConnectSocketStream();
    captureFrame();
    const captureInterval = setInterval(captureFrame, 60000 * 5);

    return () => {
      clearInterval(captureInterval);
    };
  }, [isConnected]);

  useEffect(() => {
    if (!isConnected || secondsLeft === null) return;

    if (secondsLeft <= 0) {
      onHandleStopStreaming();
      return;
    }

    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev < 0) {
          onHandleStopStreaming();
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [isConnected, secondsLeft]);

  const onHandleStartStream = async () => {
    if (streamTitle.trim().length <= 0) {
      return toast.error('Укажите заголовок стрима');
    }

    try {
      setIsLoading(true);

      const response = await axiosPrivate
        .post('/stream/start', {
          title: streamTitle
        })
        .then((res) => res.data);

      if (response.status === 'error' && 'message' in response) {
        toast.error(response.message);
        return;
      }

      await initConnectToStream();
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Не удалось создать стрим');
      }
      console.error('[StreamBroadcaster] Error initializing stream:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const onOffUserLocaleTrackings = async () => {
    try {
      localMicrophoneTrack?.stop();
      localMicrophoneTrack?.close();

      localCameraTrack?.stop();
      localCameraTrack?.close();

      setCamera(false);
      setMic(false);

      await rtcClient.leave();
    } catch (error) {
      console.error('[StreamBroadcaster] error to run onOffUserLocaleTrackings:', error);
    }
  };

  const onHandleStopStreaming = async () => {
    try {
      if (!streamId) {
        toast.error('Ошибка отключения ID не найден');
        throw new Error('Ошибка отключения streamId не найден');
      }

      if (streamChatService.isConnected()) {
        streamChatService.leaveStream(streamId);
        streamChatService.disconnect();
      }

      const res = await axiosPrivate.delete(`/stream/${streamId}`).then((res) => res.data);

      if (res.status === 'error' && 'message' in res) {
        return toast.error(res.message);
      }

      const sendGift = res.data.stream_chat.filter((c) => c.type === 'gift');

      setStartedAt(res.data.startedAt);
      setGiftsCount(sendGift.length ?? 0);
      setViewerCount(res.data.maxViewers ?? 0);
    } catch (err) {
      console.error('[StreamBroadcaster] error to run onHandleStopStreaming', err);
    } finally {
      setAgoraToken('');
      setAgoraChannel('');
      setStreamState(configStreamState.disabled);
    }
  };

  const onBackToStreamList = async () => {
    try {
      await onOffUserLocaleTrackings();
    } catch (error) {
      console.error('[StreamBroadcaster] error to run onBackToStreamList:', error);
    } finally {
      navigate(-1);
    }
  };

  const onHandleSwitchCamera = async () => {
    console.log('[StreamBroadcaster] localCameraTrack', localCameraTrack);
    if (!localCameraTrack) {
      return toast.error('Сначала запустите стрим, чтобы переключить камеру');
    }

    const devices = await AgoraRTC.getCameras();
    console.log('[StreamBroadcaster] devices', devices);
    if (devices.length < 2) {
      return toast.error('Нет доступных камер для переключения');
    }

    const currentId = localCameraTrack.getTrackLabel();
    const currentIndex = devices.findIndex((d) => currentId.includes(d.label));

    const nextIndex = currentIndex === 0 ? 1 : 0;
    setCameraIndex(nextIndex);
    // alert(`Switching camera index: ${currentIndex} -> ${nextIndex}`);

    await localCameraTrack.setDevice(devices[nextIndex].deviceId);
  };

  const initConnectSocketStream = async () => {
    try {
      const authToken = token || localStorage.getItem('auth_token');
      streamChatService.connect(authToken);

      // Слушаем новые сообщения
      const messageUnsubscribe = streamChatService.onMessage((data) => {
        console.log('[StreamViewer] Received message:', data);
        if (data.type === 'message' && data.data) {
          const formattedMessage = {
            _id: data.data._id || Date.now(),
            username: data.data.username || 'User',
            signed_thumbnail: data.data.signed_thumbnail,
            message: data.data.message,
            timestamp: new Date(data.data.timestamp || Date.now()).toLocaleTimeString(),
            role: data.data.role
          };
          setChatMessages((prev) => [...prev, formattedMessage]);
        }
      });

      // Слушаем историю чата
      const historyUnsubscribe = streamChatService.onHistory((data) => {
        console.log('[StreamBroadcaster] Received chat history:', data);
        if (data?.messages) {
          const formattedMessages = data.messages.reverse();
          setChatMessages(formattedMessages);
        }
      });

      // Слушаем обновления количества зрителей
      const viewerCountUnsubscribe = streamChatService.onViewerCount((data) => {
        console.log('[StreamBroadcaster] Viewer count:', data);
        if (data.currentViewers !== undefined) {
          setViewerCount(data.currentViewers);
        }
      });

      // Слушаем подарки
      const giftUnsubscribe = streamChatService.onGift((data) => {
        console.log('[StreamBroadcaster] Gift received:', data);
        setChatMessages((prev) => [...prev, data.message]);
      });

      // Слушаем ошибки
      const errorUnsubscribe = streamChatService.onError((error) => {
        console.error('[StreamBroadcaster] Chat error:', error);
        toast.error(error.message || 'Chat error occurred');
      });

      // Присоединяемся к чату стрима
      streamChatService.joinStream(streamId);

      return () => {
        console.log('[StreamBroadcaster] Cleaning up chat listeners');
        messageUnsubscribe();
        historyUnsubscribe();
        viewerCountUnsubscribe();
        giftUnsubscribe();
        errorUnsubscribe();
      };
    } catch (error) {
      console.error('[StreamBroadcaster] Error initializing stream socket:', error);
      toast.error('Не удалось установить соединение!');
    }
  };

  const initConnectToStream = async () => {
    setIsLoading(true);
    try {
      const myStream = await fetchMyActiveStream();
      if (myStream) {
        setSecondsLeft(myStream.minutesLeft * 60);
        await fetchAgoraToken(myStream.stream?._id);
      }
    } catch (error) {
      console.error('[StreamBroadcaster] Error initConnectToStream:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMyActiveStream = async () => {
    try {
      const res = await axiosPrivate.get('/stream/my').then((res) => res.data);
      return {
        stream: res.data?.items[0] || null,
        minutesLeft: res.data?.minutesLeft
      };
    } catch (error) {
      console.error('[StreamBroadcaster] Error fetchMyActiveStream:', error);
    }
  };

  const fetchAgoraToken = async (streamId) => {
    if (streamId === null || streamId === undefined) {
      return;
    }
    try {
      const res = await axiosPrivate.get(`/stream/${streamId}/token`).then((res) => res.data);
      setAgoraToken(res.data.token);
      setAgoraChannel(res.data.channel);
      setStreamId(streamId);
    } catch (error) {
      console.error('[StreamBroadcaster] Error fetchAgoraToken:', error);
    }
  };

  const captureFrame = async () => {
    try {
      if (!localCameraTrack) return;

      const videoTrack = localCameraTrack.getMediaStreamTrack();

      const imageCapture = new ImageCapture(videoTrack);
      const bitmap = await imageCapture.grabFrame();

      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(bitmap, 0, 0);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.8));

      if (!blob) {
        console.warn('[captureFrame] canvas.toBlob returned null');
        return;
      }

      const formData = new FormData();
      formData.append('file', blob);

      await axiosPrivate.patch(`/stream/${streamId}/thumbnail`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    } catch (error) {
      console.error('[captureFrame] Error sending capture:', error);
    }
  };

  const formattedTime = useMemo(() => {
    if (secondsLeft === null) return { m: 0, s: 0 };
    const minutes = Math.floor(secondsLeft / 60);
    const seconds = secondsLeft % 60;

    return { m: minutes, s: seconds };
  }, [secondsLeft]);

  const activeStreamTime = useMemo(() => {
    if (startedAt === null)
      return {
        m: String(0),
        s: String(0).padStart(2, '0')
      };

    const diffMs = Date.now() - new Date(startedAt).getTime();

    const totalSeconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return {
      m: String(minutes),
      s: String(seconds).padStart(2, '0')
    };
  }, [startedAt]);

  const renderItems = useCallback(() => {
    switch (streamState) {
      case configStreamState.created:
        return {
          header: (
            <div className="flex items-center absolute top-5 right-4 left-4 z-10">
              <button className="ml-auto p-4 rounded-full bg-white/10" onClick={onBackToStreamList}>
                <img src="/icons/close.svg" alt="close" />
              </button>
            </div>
          ),
          main: (
            <label
              className="absolute flex flex-col z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full px-4"
              htmlFor="title"
            >
              <input
                id="title"
                type="text"
                placeholder="Введите заголовок..."
                maxLength={30}
                value={streamTitle}
                onChange={(e) => setStreamTitle(e.target.value)}
                onFocus={() => setStreamState(configStreamState.edit)}
                className="
      w-full
      max-w-[90vw]
      mx-auto
      bg-transparent
      text-center
      font-medium
      text-[clamp(18px,5vw,28px)]
      whitespace-nowrap
      overflow-hidden
      text-ellipsis
      focus-visible:outline-none
      placeholder:text-white
    "
              />
              <p className="text-lg text-center font-medium mt-2">(не более 30 символов)</p>
            </label>
          ),
          footer: (
            <div className="flex items-center justify-center gap-10 absolute bottom-5 right-4 left-4  z-10">
              <button
                className="flex items-center justify-center w-full h-16 p-2 bg-[#A1F69E] text-[#010D0D] text-lg font-medium rounded-full"
                onClick={onHandleStartStream}
              >
                Начать стрим
              </button>
              <button onClick={onHandleSwitchCamera} className="shrink-0 p-5 rounded-full bg-white/20">
                <img src="/icons/wrap.svg" alt="wrap" />
              </button>
            </div>
          )
        };
      case configStreamState.edit:
        return {
          header: (
            <div className="flex items-center justify-between absolute top-5 right-4 left-4 z-10">
              <button className="text-lg font-medium" onClick={() => setStreamState(configStreamState.created)}>
                Отмена
              </button>
              <button className="text-lg font-medium" onClick={() => setStreamState(configStreamState.created)}>
                Готово
              </button>
            </div>
          ),
          main: (
            <label className="absolute flex flex-col z-10 top-1/3 left-1/2 -translate-x-1/2 w-full" htmlFor="title">
              <input
                id="title"
                type="text"
                placeholder="Введите заголовок..."
                maxLength={30}
                value={streamTitle}
                onChange={(e) => setStreamTitle(e.target.value)}
                onFocus={() => setStreamState(configStreamState.edit)}
                className="
      w-full
      max-w-[90vw]
      mx-auto
      bg-transparent
      text-center
      font-medium
      text-[clamp(18px,5vw,28px)]
      whitespace-nowrap
      overflow-hidden
      text-ellipsis
      focus-visible:outline-none
      placeholder:text-white
    "
              />
            </label>
          )
        };
      case configStreamState.active:
        return {
          header: (
            <div className="flex items-center gap-1 flex-wrap justify-between absolute top-5 right-4 left-4 z-10">
              <div className="flex items-center justify-between gap-2 bg-white/10 rounded-full px-2 py-[6px] min-w-[200px]">
                <span className="bg-[#CE5D49] text-xs font-semibold uppercase p-2 rounded-[46px]">СТРИМ</span>
                <div className="flex items-center gap-1">
                  <img src="/icons/eye.svg" alt="eye" />
                  <p className="text-sm">{formatViewers(viewerCount)}</p>
                </div>
                <div className="flex items-center gap-1">
                  <img src="/icons/timer.svg" alt="eye" />
                  <p className="text-sm">{`${formattedTime.m}:${formattedTime.s}`}</p>
                </div>
              </div>
              <button className="text-lg font-medium" onClick={onHandleStopStreaming}>
                Завершить
              </button>
            </div>
          ),
          footer: (
            <div className="flex items-end justify-between gap-2 absolute bottom-5 right-4 left-4 z-10">
              <ul className="max-h-[250px] overflow-auto space-y-3">
                {chatMessages.map((m) => (
                  <li key={m._id} className="flex items-start gap-2">
                    <img src={m.signed_thumbnail} className="size-8 object-cover rounded-full shrink-0" alt="avatar" />
                    <div className="min-w-0">
                      <p className="text-white text-sm truncate min-w-0">{m.username}</p>
                      {m.type !== 'gift' ? (
                        <p className="text-sm font-semibold break-words">{m.message}</p>
                      ) : (
                        <img src={m.gift.imageUrl} className="w-[40px] h-[40px]" />
                      )}
                    </div>
                  </li>
                ))}
                <li ref={bottomRef} />
              </ul>
              <button onClick={onHandleSwitchCamera} className="shrink-0 p-5 rounded-full bg-white/20 hover:rotate-180">
                <img src="/icons/wrap.svg" alt="wrap" />
              </button>
            </div>
          )
        };
      case configStreamState.disabled:
        return {
          header: (
            <div className="flex items-center absolute top-5 right-4 left-4 z-20">
              <button className="mr-auto p-4 rounded-full bg-white/10" onClick={onBackToStreamList}>
                <img src="/icons/close.svg" alt="close" />
              </button>
            </div>
          ),
          main: (
            <div className="absolute z-10 inset-0 bg-black/80 flex flex-col gap-9 items-center justify-center">
              <h2 className="text-center text-[28px] font-medium max-w-[268px]">Прямая трансляция завершена</h2>
              <div className="space-y-8 w-full">
                <div className="flex flex-col gap-3 items-center">
                  <p className="font-medium text-center">Максимальное количество зрителей:</p>
                  <div className="flex items-center gap-1">
                    <img className="size-5" src="/icons/eye-green.svg" alt="eye" />
                    <p className="text-2xl">{formatViewers(viewerCount)}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-3 items-center">
                  <p className="font-medium text-center">Вы были в прямом эфире:</p>
                  <div className="flex items-center gap-1">
                    <img className="size-5" src="/icons/timer-green.svg" alt="eye" />
                    <p className="text-2xl">{`${activeStreamTime.m} мин ${activeStreamTime.s} сек`}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-3 items-center">
                  <p className="font-medium text-center">Вам подарили подарков:</p>
                  <div className="flex items-center gap-1">
                    <img className="size-5" src="/icons/gifts.png" alt="eye" />
                    <p className="text-2xl">{giftsCount}</p>
                  </div>
                </div>
              </div>
            </div>
          )
        };
      default:
        return { header: null, main: null, footer: null };
    }
  }, [streamState, streamTitle, activeStreamTime, formattedTime, chatMessages, viewerCount]);

  if (isLoadStream) {
    return (
      <div className="flex items-center justify-center min-h-[100vh]">
        <DotSpinner />
      </div>
    );
  }

  return (
    <div className="relative w-screen h-full bg-[#022424] text-white">
      <LocalUser
        audioTrack={localMicrophoneTrack}
        cameraOn={cameraOn}
        micOn={micOn}
        playAudio={false}
        videoTrack={localCameraTrack}
        mirror={cameraIndex === 1 ? false : true}
        style={{ width: '100%', height: '100%', transform: cameraIndex === 1 ? 'scaleX(-1)' : '' }}
      />
      {renderItems().header}
      {renderItems().main}
      {renderItems().footer}
      {isLoadingAgoraJoin && <div className="bg-white/10 border-[#A1F69E] border-2 border-l-white">loading</div>}
    </div>
  );
};

export default InteractiveLiveStreaming;
